/**
 * useDialerDomEvents — Listeners DOM globaux du dialer.
 *
 * 2026-06-04 (Fowler etape 4/5 du decoupage) — Extrait du god-context
 * `DialerContext.tsx` selon la carte des dependances §1.6/§5.
 *
 * Responsabilite unique :
 *   "Attacher / detacher les 4 listeners globaux du dialer et appeler les
 *    callbacks fournis quand un evenement DOM declenche une action UI."
 *
 * Listeners :
 *   - 'keydown' → focus search input via touche '/'
 *   - 'openProspectDetail' → ouvre une modale prospect
 *   - 'revcall:voicemail_detected' → cleanup UI + invalidate parallel-calls
 *
 * Le hook ne touche AUCUN state directement : il appelle les callbacks
 * passes en argument. Ca le rend testable hors React (callbacks mockes)
 * et evite de capturer un god-state via closure.
 */

import { isTypingTarget } from '@/lib/keyboard';
import { useEffect } from 'react';
// @ts-ignore — react-query est en .js sans types stricts
import type { QueryClient } from '@tanstack/react-query';

interface UseDialerDomEventsArgs {
  /** Appele quand l'utilisateur tape '/' hors champ texte. */
  focusSearchInput?: () => void;
  /** Appele quand un autre composant dispatche 'openProspectDetail'. */
  onOpenProspectDetail: (detail: unknown) => void;
  /** Appele quand le backend STT remonte un repondeur detecte. */
  onVoicemailDetected: (event: { callId?: string; transcript?: string }) => void;
  /** QueryClient pour invalidation post-voicemail. */
  queryClient: QueryClient;
}

export function useDialerDomEvents({
  focusSearchInput,
  onOpenProspectDetail,
  onVoicemailDetected,
  queryClient,
}: UseDialerDomEventsArgs): void {
  // Keyboard shortcut '/': open and focus the list search (VFFilterBar listens).
  // Never while typing in a field: '/' must stay typable (dates, URLs, notes).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (focusSearchInput) {
        e.preventDefault();
        focusSearchInput();
        return;
      }
      // Only where a list search exists; elsewhere the key stays the
      // browser's (Firefox quick find).
      if (!document.querySelector('[data-list-search]')) return;
      e.preventDefault();
      window.dispatchEvent(new Event('xdial-focus-list-search'));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusSearchInput]);

  // Listen for prospect detail opening events (dispatch depuis d'autres composants).
  useEffect(() => {
    const handler = (event: Event) => {
      onOpenProspectDetail((event as CustomEvent).detail);
    };
    window.addEventListener('openProspectDetail', handler as EventListener);
    return () =>
      window.removeEventListener('openProspectDetail', handler as EventListener);
  }, [onOpenProspectDetail]);

  // Custom STT voicemail detection (backend dispatch 'revcall:voicemail_detected').
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      onVoicemailDetected(detail);
      queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
    };
    window.addEventListener('revcall:voicemail_detected', handler as EventListener);
    return () =>
      window.removeEventListener(
        'revcall:voicemail_detected',
        handler as EventListener,
      );
  }, [onVoicemailDetected, queryClient]);
}
